import React, { useEffect, useState } from 'react';
import { Copy } from '@grubhubprod/gh-cookbook-react';
import {
  useSignupStatus,
  SignupStep,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ROLES } from 'src/types/Employee';
import { useDinerSearch, SearchStatus } from 'src/utilities/useSearchHooks';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import { ConfirmationSummaryRow } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationSummaryRow/ConfirmationSummaryRow';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';

interface IPeopleConfirmationCardProps {
  setNumberOfPeople: (numberOfPeople: number) => void;
  onDataLoaded: () => void;
}

export function PeopleConfirmationCard({
  setNumberOfPeople,
  onDataLoaded,
}: IPeopleConfirmationCardProps): JSX.Element {
  const { corpId, getStepNumber } = useSignupStatus();
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [hasError, setHasError] = useState(false);

  const {
    totalResults: totalDiners,
    doSearch: getCorpDiners,
    searchStatus,
  } = useDinerSearch(corpId);

  function searchForDiners() {
    setHasError(false);
    setIsLoadingData(true);
    // we don't want to include the admin in the diner count
    getCorpDiners({ diner_role: ROLES.DINER });
  }
  useEffect(() => {
    if (corpId) {
      searchForDiners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (searchStatus === SearchStatus.COMPLETE) {
      setNumberOfPeople(totalDiners);
      setIsLoadingData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus, totalDiners]);

  useEffect(() => {
    if (totalDiners >= 0) {
      onDataLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDiners]);

  function getLinkText() {
    if (isLoadingData) {
      return null;
    }

    if (totalDiners > 0) {
      return 'Edit';
    }

    return 'Add';
  }

  return (
    <ConfirmationStepCardContainer
      stepName="People"
      isLoadingData={isLoadingData}
      hasError={searchStatus === SearchStatus.ERROR || hasError}
      reloadData={searchForDiners}
      useSecondaryText={totalDiners === 0}
      linkText={getLinkText()}
      stepNumber={getStepNumber(SignupStep.PEOPLE)}
    >
      {totalDiners > 0 ? (
        <ConfirmationSummaryRow label="Total">
          {totalDiners} {totalDiners > 1 ? 'People' : 'Person'}
        </ConfirmationSummaryRow>
      ) : (
        <Copy variant="body" color="secondaryText">
          Don’t forget to add people to activate your {entityName.singular}
        </Copy>
      )}
    </ConfirmationStepCardContainer>
  );
}
